import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  useGetShop,
  getGetShopQueryKey,
} from '@/api/generated/v2-shop/v2-shop'
import {
  useGetCatalogConfig,
  getGetCatalogConfigQueryKey,
} from '@/api/generated/v2-catalog/v2-catalog'
import type { ShopResponse as OrvalShopResponse, CatalogConfigResponse } from '@/api/schemas'
import { FLB_PARTNER_GUIDE_ANCHOR_ID, usePartnerStatus } from '@/features/setup/FlbPartnerShareStatus'
import { MetaFLBCard } from '@/features/setup/MetaFLBCard'
import { MetaManualCard } from '@/features/setup/MetaManualCard'
import { SetupPage } from '@/features/setup/SetupPage'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs'
import { CopyableId } from '@/components/guide/CopyableId'
import { GuideStep } from '@/components/guide/GuideStep'
import { TipBox } from '@/components/guide/TipBox'
import { auth } from '@/lib/auth'
import { useShop } from '@/lib/shopContext'

/**
 * 카탈로그 표시 라벨 — 캐시된 실제 이름이 있으면 "이름 (id)", 없으면 "id".
 * meta_catalog_name 은 orval 재생성 전이라 generated 타입엔 없을 수 있어 옵셔널로 read.
 */
function buildCatalogInfo(
  config:
    | { meta_catalog_id?: string | null; meta_catalog_name?: string | null }
    | null
    | undefined,
): string | undefined {
  if (!config?.meta_catalog_id) return undefined
  const name = config.meta_catalog_name
  return name
    ? `카탈로그: ${name} (${config.meta_catalog_id})`
    : `카탈로그: ${config.meta_catalog_id}`
}

export const Route = createFileRoute('/_dashboard/setup/meta-catalog')({
  component: MetaCatalogSetupPage,
})

function MetaCatalogSetupPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { setCatalogConnected } = useShop()
  const isAuthenticated = auth.isAuthenticated()

  // --- Orval Query 훅: Shop 정보 조회 ---
  const { data: shopRaw, isLoading: shopLoading } = useGetShop({
    query: { enabled: isAuthenticated },
  })
  const shop = (shopRaw ?? null) as OrvalShopResponse | null

  // --- Orval Query 훅: 카탈로그 설정 조회 ---
  const { data: catalogConfigRaw, isLoading: catalogLoading } = useGetCatalogConfig({
    query: { enabled: isAuthenticated },
  })
  const catalogConfig = (catalogConfigRaw ?? null) as CatalogConfigResponse | null

  // 파트너 공유 상태 — 미등록이면 가이드를 자동으로 펼친다.
  // (FlbPartnerShareStatus 배너와 같은 queryKey라 네트워크 요청은 dedup 된다)
  const flbCatalogConnected =
    !!shop?.meta_flb_catalog_connected && !!catalogConfig?.meta_catalog_id
  const { data: partnerStatus } = usePartnerStatus('catalog', flbCatalogConnected)
  const partnerNotRegistered = partnerStatus?.reason === 'not_registered'

  // --- Hook 순서 보장: 모든 useState를 early return 앞에 배치 ---
  // 기본 탭은 간편 연동(FLB). 레거시도 간편연동 탭을 먼저 보여줘 전환을 유도한다
  // (직접 입력은 탭 클릭으로 접근하는 관리용).
  const [activeTab, setActiveTab] = useState('flb')

  // 인증 안 된 경우 리다이렉트
  useEffect(() => {
    if (!isAuthenticated) {
      navigate({ to: '/' })
    }
  }, [navigate, isAuthenticated])

  // 카탈로그 연결 상태를 컨텍스트에 동기화
  useEffect(() => {
    if (catalogConfig) {
      setCatalogConnected(!!catalogConfig.meta_catalog_id)
    }
  }, [catalogConfig, setCatalogConnected])

  const loading = shopLoading || catalogLoading

  if (loading || !shop) {
    return (
      <div className="flex items-center justify-center py-20 min-h-[60vh]">
        <Loader2 className="h-6 w-6 animate-spin text-outline-variant" />
      </div>
    )
  }

  // 연동 상태: FLB 토큰 기반으로 판별
  const isFLBConnected = !!shop.meta_flb_catalog_connected && !!catalogConfig?.meta_catalog_id
  // FLB 토큰 회수(code190) 감지 — 연동됨이어도 카탈로그 동기화가 실패 중 (재연동 배너).
  // 광고의 meta_flb_ads_token_invalid 와 대칭. orval 스펙 재생성 보류 중이라
  // 로컬 타입 보강으로 읽는다.
  const flbTokenInvalid = Boolean(
    (shop as { meta_flb_catalog_token_invalid?: boolean }).meta_flb_catalog_token_invalid,
  )
  const isLegacy = !shop.meta_flb_catalog_connected && !!catalogConfig?.meta_catalog_id
  const isConnected = isFLBConnected || isLegacy

  // 선행 조건 없음 — 전 기능 오픈
  const prerequisiteMessage = undefined

  // 외부 링크
  const externalLink = catalogConfig?.meta_catalog_id
    ? `https://business.facebook.com/commerce/catalogs/${catalogConfig.meta_catalog_id}/products`
    : undefined

  /** FLB / 수동 카드 onSuccess 콜백 — Shop + 카탈로그 설정 쿼리 무효화 */
  const handleSuccess = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getGetCatalogConfigQueryKey() }),
    ])
  }

  // 탭에 맞춰 가이드 전환 (SetupPage 2컬럼: 가이드=좌 55% / 카드=우 45%)
  //  - 직접 입력: 파트너 권한 부여 + ID 입력 가이드
  //  - 간편 연동: 메타 로그인 안내 + (선택) 파트너 등록 + 픽셀 연결
  const guide =
    activeTab === 'manual' ? <MetaCatalogManualGuide /> : <MetaCatalogFLBGuide />

  // 수동 입력 카드 — 레거시 탭 흐름에서 사용
  const manualCard = (
    <MetaManualCard
      purpose="catalog"
      isConnected={isConnected}
      connectedInfo={isConnected ? buildCatalogInfo(catalogConfig) : undefined}
      onSuccess={handleSuccess}
      disabled={!!prerequisiteMessage}
      shop={shop}
      catalogConfig={catalogConfig}
      // CatalogConfig 응답에 실려 오는 현재 걸린 카탈로그 승인 요청.
      // pending 상태이면 카드가 "승인 대기 중" 뷰를 우선 보여준다.
      pendingRequest={catalogConfig?.pending_request ?? null}
    />
  )

  // 간편 연동 (FLB) 카드 — 신규/미연동 샵은 이 카드만 단독으로 노출한다.
  // 레거시(SU) 샵은 아래 Tabs 의 '간편 연동' 탭 안에 동일 카드가 들어간다.
  const flbCard = (
    <MetaFLBCard
      purpose="catalog"
      title="카탈로그"
      description="카탈로그를 연결하면 쇼핑몰 상품이 인스타그램 쇼핑과 메타 광고에 연동돼요"
      isConnected={isFLBConnected}
      connectedInfo={isFLBConnected ? buildCatalogInfo(catalogConfig) : undefined}
      legacyConnected={isLegacy}
      legacyInfo={isLegacy ? buildCatalogInfo(catalogConfig) : undefined}
      tokenInvalid={flbTokenInvalid}
      onSuccess={handleSuccess}
      disabled={!!prerequisiteMessage}
      disabledMessage={prerequisiteMessage}
    />
  )

  return (
    <SetupPage
      title="메타 카탈로그 연동"
      connected={isConnected}
      prerequisiteMessage={prerequisiteMessage}
      externalLink={externalLink}
      externalLinkLabel="메타 카탈로그 관리자"
      guide={guide}
      forceGuideOpen={partnerNotRegistered}
      warningBadge={partnerNotRegistered ? '파트너 공유 필요' : undefined}
      bare
    >
      {/* 레거시(SU 직접입력) 샵만 탭 2개(직접입력 관리 + FLB 전환)를 노출한다.
          그 외 신규/FLB 샵은 탭 없이 FLB(간편 연동) 카드만 단독 노출 —
          직접 입력은 레거시 전용이라 신규에게는 진입점 자체를 숨긴다.
          (manual 카드/탭 코드는 나중에 되살릴 수 있게 그대로 보존) */}
      {isLegacy ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="flb">간편 연동</TabsTrigger>
            <TabsTrigger value="manual">직접 입력</TabsTrigger>
          </TabsList>

          {/* 간편 연동 (FLB) — 메타 로그인 한 번으로 연동 */}
          <TabsContent value="flb">{flbCard}</TabsContent>

          {/* 직접 입력 (수동) */}
          <TabsContent value="manual">{manualCard}</TabsContent>
        </Tabs>
      ) : (
        // 신규/FLB 샵 — 탭 없이 간편 연동 카드만 단독 노출
        flbCard
      )}
    </SetupPage>
  )
}

// ---------------------------------------------------------------------------
// 직접 입력 탭 전용 가이드
// ---------------------------------------------------------------------------

/** 메타 카탈로그 직접 입력 가이드 */
function MetaCatalogManualGuide() {
  return (
    <div className="space-y-2">
      <GuideStep number={1} title="Meta 파트너 권한 부여">
        <p>
          메타 비즈니스 설정 → 사용자 → 파트너로 이동해요.
        </p>
        <ol className="list-decimal list-inside space-y-1.5 text-sm mt-2">
          <li>
            Insight Wave를 파트너로 추가해요.
            {/* 진입 경로 분기 — 다른 연동에서 이미 파트너를 추가했을 수 있다 */}
            <ul className="list-disc list-outside pl-6 space-y-1.5 mt-1.5">
              <li>
                처음 연동하는 경우: <strong>추가</strong> → <strong>파트너에게 자산에 대한 액세스 권한 부여</strong> 선택
                → 파트너 비즈니스 ID에 <CopyableId value="1081832460400655" /> 입력 후 다음
              </li>
              <li>
                이미 Insight Wave가 파트너 목록에 있는 경우: 목록에서 <strong>Insight Wave</strong> 선택
                → <strong>자산 할당</strong> 버튼 클릭
              </li>
            </ul>
          </li>
          <li>자산 및 권한 할당에서 <strong>카탈로그</strong> 선택 → <strong>전체 관리 권한</strong> 토글 켜기 → 자산 할당</li>
          <li>"Insight Wave에서 내 비즈니스의 대행사 역할을 함" 체크 후 다음 (안 뜨면 건너뛰기)</li>
        </ol>
        <TipBox type="warning">
          카탈로그가 없다면 커머스 관리자에서 먼저 만들어야 해요. 이때 반드시{' '}
          <strong>'실물 제품'</strong> 유형을 선택해주세요. '기타' 등 다른 유형으로
          만들면 상품이 연동되지 않고, 이 유형은 나중에 바꿀 수 없어요.
        </TipBox>
      </GuideStep>
      <GuideStep number={2} title="카탈로그 ID 확인">
        {/* 온라인 기수분들이 카탈로그 위치를 못 찾는 경우가 잦아 명시적 경로 안내 */}
        <p>
          <strong>비즈니스 포트폴리오 설정 → 데이터 소스 → 카탈로그</strong>에서 현재 광고에 사용 중인 카탈로그를 선택 → 카탈로그 ID를 복사해요.
        </p>
      </GuideStep>
      <GuideStep number={3} title="ID 입력">
        <p>
          복사한 카탈로그 ID를 '카탈로그 ID' 입력칸에 붙여넣고 저장해요. 저희가 자산 할당을 마친 뒤 승인해드리면 자동으로 연동돼요.
        </p>
      </GuideStep>
      <GuideStep number={4} title="픽셀(데이터 세트)을 카탈로그에 연결">
        {/* 카탈로그 일치율 0% 주원인 = 픽셀↔카탈로그 이벤트소스 미연결.
            우리가 자동으로 못 해주는 단계(토큰 권한 부족)라 고객이 직접 연결.
            관련: agent_docs/meta/catalog_pixel_connection.md */}
        <ol className="list-decimal list-inside space-y-1.5 text-sm mt-2">
          <li>
            커머스 관리자 → 왼쪽 메뉴 <strong>카탈로그 → 이벤트</strong>
          </li>
          <li>
            <strong>사용 가능한 데이터 세트</strong>에서 광고에 쓰는 픽셀을 선택해요.
          </li>
          <li>
            <strong>연결</strong> 버튼을 눌러요.
          </li>
        </ol>
      </GuideStep>
    </div>
  )
}

/** 메타 카탈로그 간편 연동(FLB) 가이드 — 연동 방법 + 파트너 권한 부여 + 픽셀 연결 */
function MetaCatalogFLBGuide() {
  return (
    <div className="space-y-2">
      <GuideStep number={1} title="카탈로그 연동">
        <p>
          <strong>"연동하기"</strong> 버튼을 누르고 메타 로그인 후 연동할 카탈로그를
          선택해주세요.
        </p>
        {/* 해제→재연동을 우회로로 쓰다 같은 비즈니스의 다른 연동까지 끊긴 사고(2026-09)가 있어 길을 알려준다 */}
        <p>
          목록에 없는 카탈로그는 연동을 해제하지 말고 <strong>"목록에 없는 카탈로그 추가"</strong>{' '}
          버튼을 눌러 메타 권한 창에서 더 체크해주세요. 이미 체크된 항목은 풀지 말고 그대로 두세요.
        </p>
        <TipBox type="warning">
          카탈로그가 없다면 커머스 관리자에서 먼저 만들어야 해요. 이때 반드시{' '}
          <strong>'실물 제품'</strong> 유형을 선택해주세요. '기타' 등 다른 유형으로
          만들면 상품이 연동되지 않고, 이 유형은 나중에 바꿀 수 없어요.
        </TipBox>
      </GuideStep>
      {/* 홈 '확인이 필요한 항목' 배너의 '가이드 보기'가 hash 로 이 스텝에 걸어 들어온다.
          scroll-mt 로 상단 고정 헤더에 가려지지 않게 여유를 둔다. */}
      <div id={FLB_PARTNER_GUIDE_ANCHOR_ID} className="scroll-mt-24">
        <GuideStep number={2} title="파트너 권한 부여">
          <p>
            원활한 분석·지원을 위해 Insight Wave를 파트너로 등록하고 자산을 공유해주세요.
          </p>
          <ol className="list-decimal list-inside space-y-1.5 text-sm mt-2">
            <li>
              메타 비즈니스 설정 → 사용자 → 파트너에서 <strong>추가</strong> →{' '}
              <strong>파트너에게 자산에 대한 액세스 권한 부여</strong> 선택 → 파트너 비즈니스
              ID에 <CopyableId value="1081832460400655" /> 입력
            </li>
            <li>
              <strong>카탈로그</strong>를 선택해 공유해요.
            </li>
          </ol>
        </GuideStep>
      </div>
      <GuideStep number={3} title="픽셀(데이터 세트)을 카탈로그에 연결">
        {/* 카탈로그 일치율 0% 주원인 = 픽셀↔카탈로그 이벤트소스 미연결.
            FLB 연동이어도 이 단계는 고객이 직접 해야 한다(토큰 권한 밖). */}
        <ol className="list-decimal list-inside space-y-1.5 text-sm mt-2">
          <li>
            커머스 관리자 → 왼쪽 메뉴 <strong>카탈로그 → 이벤트</strong>
          </li>
          <li>
            <strong>사용 가능한 데이터 세트</strong>에서 광고에 쓰는 픽셀을 선택해요.
          </li>
          <li>
            <strong>연결</strong> 버튼을 눌러요.
          </li>
        </ol>
      </GuideStep>
    </div>
  )
}

/** 메타 카탈로그 가이드 (간편 연동용, 미사용이지만 보존) */
function MetaCatalogGuide() {
  return (
    <div className="space-y-2">
      <GuideStep number={1} title="카탈로그 연동">
        <p>
          <strong>"연동하기"</strong> 버튼을 누르면 메타 로그인 후
          카탈로그를 선택할 수 있어요.
        </p>
        <TipBox type="info">
          카탈로그가 없다면 메타 Commerce Manager에서 먼저 만들어요.
        </TipBox>
      </GuideStep>
    </div>
  )
}
